import Image from '@tiptap/extension-image'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { ResizableImageView } from './ResizableImageView'
import type { ImageAlignment, ImageFloat } from './types'
import './types' // Import type declarations

export interface ResizableImageOptions {
  inline: boolean
  allowBase64: boolean
  HTMLAttributes: Record<string, unknown>
}

export const ResizableImage = Image.extend<ResizableImageOptions>({
  name: 'resizableImage',

  addOptions() {
    return {
      ...this.parent?.(),
      inline: false,
      allowBase64: true,
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => {
          const width = element.getAttribute('width')
          return width ? parseInt(width, 10) : null
        },
        renderHTML: (attributes) => {
          if (!attributes.width) return {}
          return { width: attributes.width }
        },
      },
      height: {
        default: null,
        parseHTML: (element) => {
          const height = element.getAttribute('height')
          return height ? parseInt(height, 10) : null
        },
        renderHTML: (attributes) => {
          if (!attributes.height) return {}
          return { height: attributes.height }
        },
      },
      alignment: {
        default: 'center',
        parseHTML: (element) => element.getAttribute('data-alignment') || 'center',
        renderHTML: (attributes) => {
          return { 'data-alignment': attributes.alignment }
        },
      },
      float: {
        default: 'none',
        parseHTML: (element) => element.getAttribute('data-float') || 'none',
        renderHTML: (attributes) => {
          return { 'data-float': attributes.float }
        },
      },
    }
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setImageAlignment:
        (alignment: ImageAlignment) =>
        ({ commands }) => {
          return commands.updateAttributes('resizableImage', {
            alignment,
            float: 'none', // Clear float when setting alignment
          })
        },
      setImageFloat:
        (float: ImageFloat) =>
        ({ commands }) => {
          return commands.updateAttributes('resizableImage', { float })
        },
      setImageSize:
        (width: number | null, height: number | null) =>
        ({ commands }) => {
          return commands.updateAttributes('resizableImage', { width, height })
        },
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView)
  },
})
